import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { UpdateVariableBenefitConfigurationSubmission } from '../../../../../../models/submissions/update-variable-benefit-configuration-submission';
import { VariableBenefitPaymentChangeSubmission } from '../../../../../../models/submissions/variable-benefit-payment-change-submission';
import { VariableBenefitDestinationJurisdictionDetail } from '../../../../../../models/submissions/variable-benefit-destination-jurisdiction-detail';
import {
  VbPaymentJurisdictionDetailsUtils,
  VbPaymentSubmissionData
} from '../../utils/vb-payment-jurisdiction-details.utils';

type LumpSumSubmission = UpdateVariableBenefitConfigurationSubmission | VariableBenefitPaymentChangeSubmission;
type LumpSumSubmissionData = VbPaymentSubmissionData;

interface LumpSumRow {
  jurisdictionKey: string;
  jurisdictionName: string;
  amount: number;
  isNew: boolean;
}

interface LumpSumCardState {
  isEmpty: boolean;
  paymentDate: string | null;
  rows: LumpSumRow[];
  total: number;
}

@Component({
  selector: 'app-update-vb-config-lump-sum-review-card',
  templateUrl: './update-vb-config-lump-sum-review-card.component.html',
  styleUrls: ['./update-vb-config-lump-sum-review-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateVbConfigLumpSumReviewCardComponent implements OnChanges {
  @Input({ required: true }) submission!: LumpSumSubmission;

  public showSection = false;
  public isUnchanged = false;
  public currentCard: LumpSumCardState = this.emptyCardState();
  public newCard: LumpSumCardState = this.emptyCardState();

  public ngOnChanges(): void {
    const previous = this.submission?.previous ?? null;
    const newData = this.submission?.new ?? null;

    const previousRows = this.toRows(previous, new Set());
    const newJurisdictionKeys = this.findNewJurisdictionKeys(previous, newData);
    const newRows = this.toRows(newData, newJurisdictionKeys);

    const previousHasLumpSum = previousRows.length > 0;
    const newHasLumpSum = newRows.length > 0;

    this.showSection = previousHasLumpSum || newHasLumpSum;
    this.isUnchanged =
      previousHasLumpSum && newHasLumpSum && this.areCardsEquivalent(previous, newData, previousRows, newRows);

    this.currentCard = {
      isEmpty: !previousHasLumpSum,
      paymentDate: previous?.lumpSumPaymentDesiredPaymentDate ?? null,
      rows: previousRows,
      total: this.sumAmounts(previousRows)
    };
    this.newCard = {
      isEmpty: !newHasLumpSum,
      paymentDate: newData?.lumpSumPaymentDesiredPaymentDate ?? null,
      rows: newRows,
      total: this.sumAmounts(newRows)
    };
  }

  private toRows(data: LumpSumSubmissionData | null, newJurisdictionKeys: Set<string>): LumpSumRow[] {
    const details = VbPaymentJurisdictionDetailsUtils.paymentDetails(data);
    return details
      .filter(d => d.lumpSumPaymentAmount !== null)
      .map(d => this.toRow(d, newJurisdictionKeys))
      .sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName));
  }

  private toRow(detail: VariableBenefitDestinationJurisdictionDetail, newJurisdictionKeys: Set<string>): LumpSumRow {
    const key = this.jurisdictionKey(detail);
    return {
      jurisdictionKey: key,
      jurisdictionName: detail.governingJurisdiction?.name ?? $localize`Non-Locked-In`,
      amount: detail.lumpSumPaymentAmount ?? 0,
      isNew: newJurisdictionKeys.has(key)
    };
  }

  private findNewJurisdictionKeys(
    previous: LumpSumSubmissionData | null,
    newData: LumpSumSubmissionData | null
  ): Set<string> {
    const previousKeys = new Set(
      VbPaymentJurisdictionDetailsUtils.paymentDetails(previous).map(d => this.jurisdictionKey(d))
    );
    const newDataKeys = VbPaymentJurisdictionDetailsUtils.paymentDetails(newData).map(d => this.jurisdictionKey(d));
    return new Set(newDataKeys.filter(key => !previousKeys.has(key)));
  }

  private areCardsEquivalent(
    previous: LumpSumSubmissionData | null,
    newData: LumpSumSubmissionData | null,
    previousRows: LumpSumRow[],
    newRows: LumpSumRow[]
  ): boolean {
    if (previous?.lumpSumPaymentDesiredPaymentDate !== newData?.lumpSumPaymentDesiredPaymentDate) {
      return false;
    }
    if (previousRows.length !== newRows.length) {
      return false;
    }
    return previousRows.every((prevRow, index) => {
      const newRow = newRows[index];
      return prevRow.jurisdictionKey === newRow.jurisdictionKey && prevRow.amount === newRow.amount;
    });
  }

  private sumAmounts(rows: LumpSumRow[]): number {
    return rows.reduce((sum, r) => sum + r.amount, 0);
  }

  private jurisdictionKey(detail: VariableBenefitDestinationJurisdictionDetail): string {
    return detail.governingJurisdictionId !== null ? String(detail.governingJurisdictionId) : 'non-locked-in';
  }

  private emptyCardState(): LumpSumCardState {
    return { isEmpty: true, paymentDate: null, rows: [], total: 0 };
  }
}
